// El agente VE, USA, GUARDA y EDITA los Elementos de marca (logos, elementos gráficos,
// tipografías) — ADR-2026-10-20-el-agente-usa-los-elementos-de-marca.
//
// Mismo molde que los Estilos (`verMoodboard.ts` + `editarMoodboard.ts`): acá está lo puro
// —el contrato de cada tool, qué carpeta pidió, qué bloques vuelven— y la red se inyecta
// desde `ChatAtlasContext`, que sabe de qué empresa y marca es el chat.
//
// Cómo los USA: `ver_elementos_de_marca` cuelga cada elemento que muestra del mensaje del
// agente con un id `marca-…` (como las fotos de una web o de un PDF). Con ese id va a
// `reference_images` con source "adjunto" en cualquier generación, y `adjuntoPorId` lo
// resuelve. Una tipografía se muestra —y viaja— como su HOJA DE MUESTRA.

import type { BloqueContenido } from '../pages/v2/lotes/verProductos';
import { ventanaDeFotos } from '../pages/v2/lotes/verFotos';
import type { ImagenDeLaWeb } from '../pages/v2/adjuntosDelMensaje';
import { COLORES_DE_CARPETA } from '../pages/v2/moodboardsApi';
import type { BrandAssetTipo } from '../utils/brandAssets';
import {
  FUENTES_DE_FOTO,
  MAX_FOTOS_POR_LLAMADA,
  campoDeTexto,
  colorPedido,
  creaSiNoExiste,
  describirFoto,
  fotosPedidas,
  type FotoPedida,
  type Resueltas,
} from './editarMoodboard';
import type { HerramientaChat } from './tipos';

export const NOMBRE_VER_ELEMENTOS = 'ver_elementos_de_marca';
export const NOMBRE_GUARDAR_EN_ELEMENTOS = 'guardar_en_elementos_de_marca';
export const NOMBRE_EDITAR_ELEMENTOS = 'editar_elementos_de_marca';

export interface CarpetaParaElAgente {
  id: string;
  nombre: string;
  cantidad: number;
}

export interface ElementoParaElAgente {
  id: string;
  titulo: string;
  tipo: BrandAssetTipo;
}

/** Un elemento listo para mostrar: la URL firmada de lo que ve el modelo (el original, o la
 *  hoja de muestra de una tipografía) y su path, para colgarlo del mensaje. */
export interface ElementoMostrable {
  elemento: ElementoParaElAgente;
  url: string | null;
  storagePath: string | null;
}

const IDS_DE_COLOR = COLORES_DE_CARPETA.map((c) => c.id);

/** El id con que el agente usa un elemento: `marca-` + 12 hex del id del elemento. */
export const idDeElementoEnElChat = (elementoId: string): string =>
  `marca-${elementoId.replace(/[^0-9a-f]/gi, '').toLowerCase().slice(0, 12).padEnd(12, '0')}`;

/** La etiqueta con que conviene pasarlo en `reference_images`: "Logo" activa las reglas del
 *  logo en el motor; la tipografía, su rótulo (ADR-2026-10-19-la-tipografia-viaja-como-hoja-de-muestra). */
export function etiquetaParaReferencia(e: ElementoParaElAgente): string {
  if (e.tipo === 'logo') return 'Logo';
  if (e.tipo === 'font') return `Tipografía de la marca «${e.titulo}»`;
  return `Elemento de marca: ${e.titulo}`;
}

const elementos = (n: number) => (n === 1 ? '1 elemento' : `${n} elementos`);

const normalizar = (s: string): string =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()
    .toLowerCase();

/** Qué carpeta pidió: por id, por nombre exacto, o por nombre contenido si es único. */
export function elegirCarpeta(pedido: string, carpetas: CarpetaParaElAgente[]): CarpetaParaElAgente | null {
  const q = normalizar(pedido);
  return (
    carpetas.find((c) => c.id === pedido) ??
    carpetas.find((c) => normalizar(c.nombre) === q) ??
    (() => {
      const parciales = carpetas.filter((c) => normalizar(c.nombre).includes(q));
      return parciales.length === 1 ? parciales[0] : null;
    })()
  );
}

const listaDeCarpetas = (carpetas: CarpetaParaElAgente[]): string =>
  carpetas.map((c) => `"${c.nombre}" (${elementos(c.cantidad)})`).join(', ');

const sinCarpeta = (pedido: string, carpetas: CarpetaParaElAgente[]): string =>
  carpetas.length === 0
    ? `No hay ninguna carpeta que se llame "${pedido}": la marca no tiene Elementos de marca todavía.`
    : `No hay ninguna carpeta que se llame "${pedido}". Las que hay: ${listaDeCarpetas(carpetas)}.`;

// ─── Las tres tools ────────────────────────────────────────────────────

export const HERRAMIENTA_VER_ELEMENTOS: HerramientaChat = {
  name: NOMBRE_VER_ELEMENTOS,
  description:
    'Los ELEMENTOS DE MARCA (en la app, Mi marca → Elementos de marca): carpetas con los logos, elementos gráficos y tipografías de la marca. Sin `carpeta` te lista las carpetas; con el nombre de una, te muestra sus elementos como imágenes, de a 10, cada uno con su `adjunto_id` ("marca-…") y la etiqueta para usarlo. Una tipografía se ve como su HOJA DE MUESTRA (abecedario, números y signos). No tiene UI, no cuesta créditos.\n' +
    'Usala cuando el usuario pide su logo, un gráfico o su tipografía en una pieza ("poné mi logo", "con mi tipografía", "usá el isotipo"), o pregunta qué tiene guardado. Para USAR uno en una generación: `reference_images` con source "adjunto" + ese `adjunto_id` + su `etiqueta`. Van TAL CUAL: no se redibujan, recolorean ni retocan, salvo que el usuario pida otra cosa. Método: skill `elementos-de-marca`.',
  input_schema: {
    type: 'object',
    properties: {
      carpeta: {
        type: 'string',
        description: 'Nombre (como figura en la lista) o id de la carpeta. Omitilo para listar las carpetas.',
      },
      desde: { type: 'number', description: 'Desde qué elemento empezar, contando desde 1. Default 1.' },
      cantidad: { type: 'number', description: 'Cuántos mostrar (1 a 10). Default 10.' },
    },
  },
};

export const HERRAMIENTA_GUARDAR_EN_ELEMENTOS: HerramientaChat = {
  name: NOMBRE_GUARDAR_EN_ELEMENTOS,
  description:
    'Guarda imágenes en una carpeta de los ELEMENTOS DE MARCA (logos, elementos gráficos): una que generaste en el chat, algo que adjuntó el usuario, la foto de un producto o de un modelo, o el logo. Con `crear_si_no_existe` crea la carpeta en la misma llamada. Cada imagen puede llevar su `titulo`. En una carpeta con "logo" en el nombre se guarda como logo; en el resto, como elemento gráfico. Las tipografías (archivos de fuente) no se guardan desde acá: se suben en Mi marca.\n' +
    'ESCRIBE en la marca: hacela cuando te lo pidan ("guardá este logo", "sumá ese ícono a mis elementos") o cuando lo propusiste y te dijeron que sí. Después contale en una línea qué guardaste y dónde.',
  input_schema: {
    type: 'object',
    properties: {
      carpeta: { type: 'string', description: 'Nombre o id de la carpeta donde guardar.' },
      crear_si_no_existe: {
        type: 'boolean',
        description: 'true = si no hay ninguna con ese nombre, la crea. Default false (un typo no te crea una carpeta de más).',
      },
      color: { type: 'string', description: `OPCIONAL, sólo al crear. Uno de: ${IDS_DE_COLOR.join(', ')}.` },
      imagenes: {
        type: 'array',
        description: `Qué guardar, en orden. Hasta ${MAX_FOTOS_POR_LLAMADA} por llamada.`,
        items: {
          type: 'object',
          properties: {
            source: {
              type: 'string',
              enum: [...FUENTES_DE_FOTO],
              description:
                '"generada" (`image_id`), "adjunto" (lo que adjuntó el usuario; `adjunto_id` para una puntual), "producto" (`product_id`), "modelo" (`model_id`), "logo" (el logo de la marca), "moodboard" (una foto de un estilo: `moodboard_origen` + `numero`).',
            },
            image_id: { type: 'string' },
            adjunto_id: { type: 'string' },
            product_id: { type: 'string' },
            model_id: { type: 'string' },
            moodboard_origen: { type: 'string' },
            numero: { type: 'number', description: 'Posición desde 1 (de las adjuntas, o en el estilo de origen).' },
            titulo: { type: 'string', description: 'OPCIONAL. Cómo se llama el elemento ("Isotipo blanco").' },
          },
          required: ['source'],
        },
      },
    },
    required: ['carpeta', 'imagenes'],
  },
};

export const HERRAMIENTA_EDITAR_ELEMENTOS: HerramientaChat = {
  name: NOMBRE_EDITAR_ELEMENTOS,
  description:
    'Ordena los ELEMENTOS DE MARCA: renombra o pinta una carpeta, crea una vacía (`crear_si_no_existe`), y a cada elemento —por el número que le dio ver_elementos_de_marca— le cambia el título, lo mueve a otra carpeta o lo elimina. Borrar una carpeta entera no se puede desde acá (se hace en Mi marca).\n' +
    'ESCRIBE en la marca: sólo si te lo piden o lo propusiste y te dijeron que sí. ELIMINAR es irreversible, y lo borra también del Estudio de Video: confirmá con el usuario antes, nombrando qué vas a borrar. Después contale en una línea qué cambiaste.',
  input_schema: {
    type: 'object',
    properties: {
      carpeta: { type: 'string', description: 'Nombre o id de la carpeta.' },
      crear_si_no_existe: { type: 'boolean', description: 'true = si no existe, la crea vacía.' },
      nuevo_nombre: { type: 'string', description: 'OPCIONAL. Nombre nuevo de la carpeta.' },
      color: { type: 'string', description: `OPCIONAL. Uno de: ${IDS_DE_COLOR.join(', ')}.` },
      cambios: {
        type: 'array',
        description: 'OPCIONAL. Cambios por elemento.',
        items: {
          type: 'object',
          properties: {
            numero: { type: 'number', description: 'El número del elemento en esta carpeta (el # de ver_elementos_de_marca).' },
            titulo: { type: 'string', description: 'Título nuevo.' },
            mover_a: { type: 'string', description: 'Nombre o id de la carpeta a la que se mueve.' },
            eliminar: { type: 'boolean', description: 'true = lo borra (irreversible: confirmá antes).' },
          },
          required: ['numero'],
        },
      },
    },
    required: ['carpeta'],
  },
};

export const HERRAMIENTAS_DE_ELEMENTOS_DE_MARCA = [
  HERRAMIENTA_VER_ELEMENTOS,
  HERRAMIENTA_GUARDAR_EN_ELEMENTOS,
  HERRAMIENTA_EDITAR_ELEMENTOS,
];

// ─── ver_elementos_de_marca ─────────────────────────────────────────────

export interface DepsVer {
  listar: () => Promise<CarpetaParaElAgente[]>;
  elementosDe: (carpetaId: string) => Promise<ElementoParaElAgente[]>;
  /** Firma lo que se le muestra al modelo (el original, o la hoja de una tipografía). */
  mostrable: (e: ElementoParaElAgente) => Promise<{ url: string | null; storagePath: string | null }>;
  /** Cuelga los mostrados del mensaje del agente, así `adjuntoPorId` resuelve su id. */
  registrar: (imagenes: ImagenDeLaWeb[]) => void;
  bucket: string;
}

const RECORDATORIO_DE_USO =
  'Para USAR uno en una generación: reference_images con source "adjunto" + su adjunto_id + su etiqueta. Van TAL CUAL: no se redibujan, recolorean ni retocan; la tipografía, mismas letras, peso, espaciado y color, sin efectos, y nunca se pone la hoja en la imagen. Sólo cambia lo que el usuario pida.';

export async function resolverVerElementos(input: unknown, deps: DepsVer): Promise<{ __bloques: BloqueContenido[] }> {
  const carpetas = await deps.listar();
  const pedido = campoDeTexto(input, 'carpeta');
  if (!pedido) {
    return {
      __bloques: [
        {
          type: 'text',
          text:
            carpetas.length === 0
              ? 'La marca no tiene Elementos de marca todavía. Podés guardar uno con guardar_en_elementos_de_marca (crear_si_no_existe: true); el usuario también los sube en Mi marca → Elementos de marca.'
              : `Elementos de marca: ${listaDeCarpetas(carpetas)}. Para ver los de una, volvé a llamar ver_elementos_de_marca con su nombre en \`carpeta\`.`,
        },
      ],
    };
  }
  const carpeta = elegirCarpeta(pedido, carpetas);
  if (!carpeta) return { __bloques: [{ type: 'text', text: `${sinCarpeta(pedido, carpetas)} Volvé a llamar con uno de esos nombres.` }] };

  const todos = await deps.elementosDe(carpeta.id);
  if (todos.length === 0) {
    return { __bloques: [{ type: 'text', text: `La carpeta "${carpeta.nombre}" está vacía.` }] };
  }
  const ventana = ventanaDeFotos(todos.length, input);
  const tramo = ventana.indices.map((i) => ({ elemento: todos[i], numero: i + 1 })).filter((x) => x.elemento);
  if (tramo.length === 0) {
    return { __bloques: [{ type: 'text', text: `Ese tramo no existe: "${carpeta.nombre}" tiene ${elementos(todos.length)}. Pedí un \`desde\` entre 1 y ${todos.length}.` }] };
  }
  const mostrados = await Promise.all(
    tramo.map(async ({ elemento, numero }) => {
      const m = await deps.mostrable(elemento).catch(() => ({ url: null, storagePath: null }));
      return { elemento, numero, ...m, id: idDeElementoEnElChat(elemento.id) };
    }),
  );
  deps.registrar(
    mostrados
      .filter((m) => m.storagePath)
      .map((m) => ({
        id: m.id,
        storagePath: m.storagePath as string,
        bucket: deps.bucket,
        urlOriginal: `Elementos de marca · ${carpeta.nombre} · ${m.elemento.titulo}`,
      })),
  );

  const desde = ventana.desde;
  const hasta = desde + mostrados.length - 1;
  const bloques: BloqueContenido[] = [
    {
      type: 'text',
      text: `[ELEMENTOS DE MARCA: "${carpeta.nombre}"] ${desde} a ${hasta} de ${elementos(todos.length)}: ${mostrados
        .map((m) => `#${m.numero}=${m.id} (${m.elemento.titulo}${m.elemento.tipo === 'font' ? ', tipografía' : m.elemento.tipo === 'logo' ? ', logo' : ''})`)
        .join(', ')}. ${RECORDATORIO_DE_USO}`,
    },
  ];
  for (const m of mostrados) {
    const rotulo =
      m.elemento.tipo === 'font' ? `[TIPOGRAFÍA: "${m.elemento.titulo}"] (hoja de muestra)` : `[ELEMENTO DE MARCA: "${m.elemento.titulo}"]`;
    bloques.push({
      type: 'text',
      text: `#${m.numero} adjunto_id=${m.id} · etiqueta "${etiquetaParaReferencia(m.elemento)}" · ${rotulo}${m.url ? ':' : ' (no se pudo cargar)'}`,
    });
    if (m.url) bloques.push({ type: 'image', source: { type: 'url', url: m.url } });
  }
  const quedan = todos.length - hasta;
  if (quedan > 0) {
    bloques.push({ type: 'text', text: `Quedan ${elementos(quedan)} sin mirar: volvé a llamar con desde=${hasta + 1} si te hacen falta.` });
  }
  return { __bloques: bloques };
}

// ─── guardar_en_elementos_de_marca ──────────────────────────────────────

export interface DepsGuardar {
  listar: () => Promise<CarpetaParaElAgente[]>;
  crear: (nombre: string, color: string | null) => Promise<CarpetaParaElAgente>;
  resolver: (pedidas: FotoPedida[]) => Promise<Resueltas>;
  guardar: (carpeta: CarpetaParaElAgente, url: string, titulo: string | null) => Promise<void>;
  refrescar?: () => void;
}

/** El título de cada imagen pedida, por la misma posición que `fotosPedidas` deja pasar. */
function titulosPedidos(input: unknown): Array<string | null> {
  const bruto = (input as { imagenes?: unknown } | null)?.imagenes;
  if (!Array.isArray(bruto)) return [];
  const out: Array<string | null> = [];
  for (const item of bruto) {
    if (fotosPedidas({ imagenes: [item] }).length === 0) continue;
    const t = (item as { titulo?: unknown }).titulo;
    out.push(typeof t === 'string' && t.trim() ? t.trim().slice(0, 80) : null);
    if (out.length >= MAX_FOTOS_POR_LLAMADA) break;
  }
  return out;
}

export async function resolverGuardarEnElementos(input: unknown, deps: DepsGuardar): Promise<{ __texto: string; __error?: true }> {
  const pedido = campoDeTexto(input, 'carpeta');
  if (!pedido) return { __texto: 'Falta `carpeta`: dónde guardar.', __error: true };
  const pedidas = fotosPedidas(input);
  if (pedidas.length === 0) {
    return { __texto: 'No pasaste ninguna imagen válida en `imagenes`. Cada una necesita su `source`.', __error: true };
  }
  const titulos = titulosPedidos(input);
  const carpetas = await deps.listar();
  let carpeta = elegirCarpeta(pedido, carpetas);
  if (!carpeta && !creaSiNoExiste(input)) {
    return { __texto: `${sinCarpeta(pedido, carpetas)} Si querés una nueva, volvé a llamar con crear_si_no_existe: true.`, __error: true };
  }
  // Primero las imágenes y después la carpeta: si no se encuentra ninguna, no queda una
  // carpeta vacía creada (ADR-2026-10-20-las-adjuntas-se-buscan-en-su-bucket).
  const { ok, fallidas } = await deps.resolver(pedidas);
  const noGuardadas = fallidas.map((f) => `${describirFoto(f.pedida)} (${f.motivo})`);
  if (ok.length === 0) {
    return {
      __texto: `No pude guardar: ${noGuardadas.join('; ') || 'ninguna imagen'}. No entró ninguna${carpeta ? '' : ' y no creé la carpeta'}. Revisá los ids o preguntale al usuario cuáles quería.`,
      __error: true,
    };
  }
  let creada = false;
  if (!carpeta) {
    carpeta = await deps.crear(pedido, colorPedido(input));
    creada = true;
  }
  const guardadas: string[] = [];
  for (const foto of ok) {
    const titulo = titulos[pedidas.indexOf(foto.pedida)] ?? null;
    try {
      await deps.guardar(carpeta, foto.url, titulo);
      guardadas.push(titulo ? `"${titulo}"` : describirFoto(foto.pedida));
    } catch (e) {
      noGuardadas.push(`${describirFoto(foto.pedida)} (${e instanceof Error ? e.message : String(e)})`);
    }
  }
  deps.refrescar?.();
  const partes: string[] = [];
  if (creada) partes.push(`Creé la carpeta "${carpeta.nombre}".`);
  if (guardadas.length > 0) partes.push(`Guardé en "${carpeta.nombre}": ${guardadas.join(', ')}.`);
  if (noGuardadas.length > 0) partes.push(`No pude guardar: ${noGuardadas.join('; ')}.`);
  if (guardadas.length === 0) {
    partes.push('No entró ninguna. Revisá los ids o preguntale al usuario cuáles quería.');
    return { __texto: partes.join(' '), __error: true };
  }
  partes.push('Contale al usuario qué guardaste y dónde, en una línea.');
  return { __texto: partes.join(' ') };
}

// ─── editar_elementos_de_marca ──────────────────────────────────────────

export interface CambioPedido {
  numero: number;
  titulo?: string;
  mover_a?: string;
  eliminar?: boolean;
}

export function cambiosPedidos(input: unknown): CambioPedido[] {
  const bruto = (input as { cambios?: unknown } | null)?.cambios;
  if (!Array.isArray(bruto)) return [];
  const out: CambioPedido[] = [];
  for (const item of bruto) {
    if (!item || typeof item !== 'object') continue;
    const o = item as Record<string, unknown>;
    if (typeof o.numero !== 'number' || !Number.isFinite(o.numero) || o.numero < 1) continue;
    const c: CambioPedido = { numero: Math.floor(o.numero) };
    if (typeof o.titulo === 'string' && o.titulo.trim()) c.titulo = o.titulo.trim().slice(0, 80);
    if (typeof o.mover_a === 'string' && o.mover_a.trim()) c.mover_a = o.mover_a.trim();
    if (o.eliminar === true) c.eliminar = true;
    if (c.titulo || c.mover_a || c.eliminar) out.push(c);
  }
  return out;
}

export interface DepsEditar {
  listar: () => Promise<CarpetaParaElAgente[]>;
  crear: (nombre: string, color: string | null) => Promise<CarpetaParaElAgente>;
  renombrarCarpeta: (id: string, nombre: string) => Promise<void>;
  pintarCarpeta: (id: string, color: string) => Promise<void>;
  /** En el MISMO orden que numera `ver_elementos_de_marca`. */
  elementosDe: (carpetaId: string) => Promise<ElementoParaElAgente[]>;
  renombrar: (elementoId: string, titulo: string) => Promise<void>;
  mover: (elementoId: string, carpetaId: string) => Promise<void>;
  eliminar: (elementoId: string) => Promise<void>;
  refrescar?: () => void;
}

export async function resolverEditarElementos(input: unknown, deps: DepsEditar): Promise<{ __texto: string; __error?: true }> {
  const pedido = campoDeTexto(input, 'carpeta');
  if (!pedido) return { __texto: 'Falta `carpeta`.', __error: true };
  const carpetas = await deps.listar();
  let carpeta = elegirCarpeta(pedido, carpetas);
  const hecho: string[] = [];
  const fallas: string[] = [];
  if (!carpeta) {
    if (!creaSiNoExiste(input)) {
      return { __texto: `${sinCarpeta(pedido, carpetas)} Si querés crearla, volvé a llamar con crear_si_no_existe: true.`, __error: true };
    }
    carpeta = await deps.crear(pedido, colorPedido(input));
    carpetas.push(carpeta);
    hecho.push(`creé la carpeta "${carpeta.nombre}"`);
  } else {
    const color = colorPedido(input);
    if (color) {
      await deps.pintarCarpeta(carpeta.id, color).then(
        () => hecho.push(`pinté "${carpeta!.nombre}" de ${color}`),
        (e) => fallas.push(`el color (${String(e?.message ?? e)})`),
      );
    }
  }
  const nuevoNombre = campoDeTexto(input, 'nuevo_nombre');
  if (nuevoNombre && nuevoNombre !== carpeta.nombre) {
    await deps.renombrarCarpeta(carpeta.id, nuevoNombre).then(
      () => hecho.push(`renombré "${carpeta!.nombre}" a "${nuevoNombre}"`),
      (e) => fallas.push(`el nombre (${String(e?.message ?? e)})`),
    );
  }
  const cambios = cambiosPedidos(input);
  if (cambios.length > 0) {
    const lista = await deps.elementosDe(carpeta.id);
    for (const c of cambios) {
      const el = lista[c.numero - 1];
      if (!el) {
        fallas.push(`#${c.numero} (no existe: la carpeta tiene ${elementos(lista.length)})`);
        continue;
      }
      try {
        if (c.eliminar) {
          await deps.eliminar(el.id);
          hecho.push(`eliminé "${el.titulo}"`);
          continue;
        }
        if (c.titulo && c.titulo !== el.titulo) {
          await deps.renombrar(el.id, c.titulo);
          hecho.push(`"${el.titulo}" ahora se llama "${c.titulo}"`);
        }
        if (c.mover_a) {
          const destino = elegirCarpeta(c.mover_a, carpetas);
          if (!destino) fallas.push(`mover "${el.titulo}" (${sinCarpeta(c.mover_a, carpetas)})`);
          else if (destino.id !== carpeta.id) {
            await deps.mover(el.id, destino.id);
            hecho.push(`moví "${c.titulo ?? el.titulo}" a "${destino.nombre}"`);
          }
        }
      } catch (e) {
        fallas.push(`#${c.numero} (${e instanceof Error ? e.message : String(e)})`);
      }
    }
  }
  deps.refrescar?.();
  if (hecho.length === 0) {
    return {
      __texto: fallas.length > 0 ? `No pude hacer nada: ${fallas.join('; ')}.` : 'No pediste ningún cambio: pasá `nuevo_nombre`, `color` o `cambios`.',
      __error: true,
    };
  }
  return {
    __texto: `Listo: ${hecho.join('; ')}.${fallas.length > 0 ? ` No pude: ${fallas.join('; ')}.` : ''} Los números de la carpeta pueden haber cambiado: si vas a seguir editando, mirala de nuevo con ver_elementos_de_marca. Contale al usuario qué cambiaste, en una línea.`,
  };
}
